import { Component } from 'react';
import { AlertTriangle, RotateCcw } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

/**
 * Generic React ErrorBoundary. Wraps any subtree and shows a friendly
 * fallback UI instead of a white screen when a render error occurs.
 *
 * Why this exists:
 *   Plain JS `try/catch` around JSX cannot catch errors raised during
 *   React's reconciliation (e.g. "Element type is invalid" from an
 *   undefined component, or a null-property read inside a child).
 *   Only a class component with `getDerivedStateFromError` /
 *   `componentDidCatch` can intercept those.
 *
 * Usage:
 *   <ErrorBoundary
 *     label="Expenses"
 *     onReset={() => {
 *       try { localStorage.removeItem('emb.exp.groupMode'); } catch {}
 *       window.location.reload();
 *     }}
 *   >
 *     <ExpensesPage />
 *   </ErrorBoundary>
 */
export class ErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, info) {
        // eslint-disable-next-line no-console
        console.error(`[ErrorBoundary${this.props.label ? ` · ${this.props.label}` : ''}]`, error, info);
    }

    handleReset = () => {
        this.setState({ hasError: false, error: null });
        if (typeof this.props.onReset === 'function') {
            try {
                this.props.onReset();
            } catch (e) {
                // eslint-disable-next-line no-console
                console.error('[ErrorBoundary] onReset failed:', e);
            }
        }
    };

    render() {
        if (!this.state.hasError) return this.props.children;

        const msg =
            (this.state.error && (this.state.error.message || String(this.state.error))) ||
            'Something went wrong rendering this page.';

        return (
            <div className="p-4" data-testid="error-boundary-fallback">
                <Card className="p-6 border-amber-300/60 bg-amber-50/40 dark:bg-amber-950/10">
                    <div className="flex items-start gap-3">
                        <div className="h-10 w-10 rounded-md bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400 flex items-center justify-center shrink-0">
                            <AlertTriangle size={18} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="font-heading text-lg">
                                {this.props.title || 'This section hit a snag'}
                            </div>
                            <div className="text-sm text-muted-foreground mt-1">
                                {this.props.description ||
                                    'Your data is safe. Try resetting the view — if it keeps happening, refresh the page.'}
                            </div>
                            <div className="text-[11px] font-mono text-muted-foreground/80 mt-2 break-all">
                                {msg}
                            </div>
                            <div className="mt-4 flex flex-wrap gap-2">
                                <Button
                                    size="sm"
                                    onClick={this.handleReset}
                                    data-testid="error-boundary-reset-btn"
                                >
                                    <RotateCcw size={13} className="mr-1" /> Reset view
                                </Button>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => window.location.reload()}
                                    data-testid="error-boundary-reload-btn"
                                >
                                    Reload page
                                </Button>
                            </div>
                        </div>
                    </div>
                </Card>
            </div>
        );
    }
}

export default ErrorBoundary;
